import { Button } from '@/components/ui/button';
import { useSidebar } from '@/components/ui/sidebar';
import { useLocation, useSearchParams } from 'react-router-dom';
import { UserAvatar } from './UserAvatar';
import { useTranslation } from 'react-i18next';
import { useIsFlutterWebView } from '@/hooks/useIsFlutterWebView';
import { Bot } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/** Shared app-bar height. The sidebar brand row tracks this so it does not jump between pages. */
export const APP_HEADER_HEIGHT_PX = 61;

const PAGE_TITLE_KEYS: Record<string, string> = {
  '/': 'nav.dashboard',
  '/live-map': 'nav.liveMap',
  '/devices': 'nav.fleetVehicles',
  '/history': 'nav.history',
  '/reseller': 'nav.overview',
  '/customers': 'nav.customers',
  '/vehicles': 'nav.vehicles',
  '/sim-cards': 'nav.simCards',
  '/contracts': 'nav.contracts',
  '/invoices': 'nav.invoices',
  '/field-services': 'nav.fieldServices',
  '/maintenance': 'nav.maintenance',
  '/routes': 'nav.routes',
  '/cameras': 'nav.cameras',
  '/drivers': 'nav.drivers',
  '/users': 'nav.users',
  '/geofences': 'nav.geofences',
  '/notifications': 'nav.notifications',
  '/settings': 'nav.settings',
};

const SidebarIcon = ({ collapsed }: { collapsed: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
    viewBox="0 0 24 24"
    className="h-4 w-4"
  >
    {collapsed ? (
      <>
        <path
          fill="currentColor"
          d="M19.25 7A2.25 2.25 0 0 0 17 4.75H7A2.25 2.25 0 0 0 4.75 7v10A2.25 2.25 0 0 0 7 19.25h10A2.25 2.25 0 0 0 19.25 17zm1.5 10A3.75 3.75 0 0 1 17 20.75H7A3.75 3.75 0 0 1 3.25 17V7A3.75 3.75 0 0 1 7 3.25h10A3.75 3.75 0 0 1 20.75 7z"
        />
        <path
          fill="currentColor"
          d="M12 7.25a.75.75 0 0 1 .75.75v8a.75.75 0 0 1-.75.75H8a.75.75 0 0 1-.75-.75V8A.75.75 0 0 1 8 7.25z"
        />
      </>
    ) : (
      <path
        fill="currentColor"
        d="M19.25 7A2.25 2.25 0 0 0 17 4.75H7A2.25 2.25 0 0 0 4.75 7v10A2.25 2.25 0 0 0 7 19.25h10A2.25 2.25 0 0 0 19.25 17zm-12 9V8a.75.75 0 0 1 1.5 0v8a.75.75 0 0 1-1.5 0m13.5 1A3.75 3.75 0 0 1 17 20.75H7A3.75 3.75 0 0 1 3.25 17V7A3.75 3.75 0 0 1 7 3.25h10A3.75 3.75 0 0 1 20.75 7z"
      />
    )}
  </svg>
);

interface HeaderProps {
  headerRef?: React.RefObject<HTMLElement>;
  children?: React.ReactNode;
  headerActions?: React.ReactNode;
  userInitials?: string;
  userEmail?: string;
  onLogout?: () => void;
  onAssistantClick?: () => void;
  isAssistantOpen?: boolean;
  transparent?: boolean;
}

export const Header = ({
  headerRef,
  children,
  headerActions,
  userInitials,
  userEmail,
  onLogout,
  onAssistantClick,
  isAssistantOpen,
  transparent = false,
}: HeaderProps) => {
  const { state, toggleSidebar } = useSidebar();
  const isCollapsed = state === "collapsed";
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isFlutter = useIsFlutterWebView();
  const hideSidebarButton = isFlutter || searchParams.get('hideSidebar') === '1';
  // Detail pages like /routes/:id use their section's title.
  const pageTitleKey = PAGE_TITLE_KEYS[location.pathname] ?? (location.pathname.startsWith('/routes/') ? 'nav.routes' : undefined);
  const pageTitle = pageTitleKey ? t(pageTitleKey) : '';

  return (
    <header
      ref={headerRef}
      style={{ height: APP_HEADER_HEIGHT_PX }}
      className={cn(
        "box-border flex shrink-0 items-center gap-4 border-b border-border px-4",
        transparent
          ? "absolute inset-x-0 top-0 z-20 bg-gradient-to-b from-black/50 to-transparent text-white"
          : "bg-card",
      )}
    >
      <div className="flex items-center gap-3 shrink-0">
        {!hideSidebarButton && (
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className="h-8 w-8 border border-border"
          >
            <SidebarIcon collapsed={isCollapsed} />
          </Button>
        )}
        <span className="font-semibold text-lg hidden sm:inline">{pageTitle}</span>
      </div>
      <div className="@container flex-1 min-w-0 flex items-center gap-1 sm:gap-4 flex-nowrap">
        {children}
      </div>
      <div className="flex items-center gap-1 ml-auto shrink-0">
        {headerActions}
        {onAssistantClick && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={isAssistantOpen ? "default" : "ghost"}
                size="icon"
                onClick={onAssistantClick}
                className="h-8 w-8 hidden sm:flex"
              >
                <Bot className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t("chat.title")}</p>
            </TooltipContent>
          </Tooltip>
        )}
        <div className="hidden sm:block">
          <UserAvatar initials={userInitials} email={userEmail} onLogout={onLogout} />
        </div>
      </div>
    </header>
  );
};
